import React from 'react';
import {
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  SelectChangeEvent,
  Button,
  Stack,
  Rating,
  Checkbox,
  ListItemText,
  OutlinedInput
} from '@mui/material';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import StarIcon from '@mui/icons-material/Star';
import { Filters } from '../../utils/filterUtils';

// Extended Filters interface to include edition
interface ExtendedFilters extends Filters {
  edition?: string;
}

interface FilterPanelProps {
  categories: string[];
  editions: string[];
  productTypes: string[];
  filters: ExtendedFilters;
  onUpdateFilters: (newFilters: Partial<ExtendedFilters>) => void;
  onResetFilters: () => void;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  categories,
  editions,
  productTypes,
  filters,
  onUpdateFilters,
  onResetFilters
}) => {
  const handleCategoryChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onUpdateFilters({ 
      categories: typeof value === 'string' ? value.split(',') : value 
    });
  };
  
  const handleIntensityChange = (event: SelectChangeEvent<string>) => {
    onUpdateFilters({ intensity: event.target.value });
  };
  
  const handleCoolingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onUpdateFilters({ hasCooling: event.target.checked });
  };
  
  const handleNewArrivalsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onUpdateFilters({ isNewArrival: event.target.checked });
  };
  
  const handleResetFilters = () => {
    onResetFilters();
  };

  // Common styles for all select components
  const selectSx = {
    '& .MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    }
  };
  
  return (
    <Paper sx={{ 
      p: 2, 
      mb: 3,
      position: 'sticky',
      top: 120, // Header height (64px) + TabPanel height (~56px)
      zIndex: 9,
      maxHeight: 'calc(100vh - 120px)',
      overflowY: 'auto'
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Filters</Typography>
        <Button 
          size="small" 
          onClick={handleResetFilters}
          disabled={!filters.categories?.length && !filters.intensity && filters.hasCooling === undefined && !filters.isNewArrival}
        >
          Reset Filters
        </Button>
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth size="small" sx={selectSx}>
          <InputLabel id="product-type-select-label" shrink>Product Type</InputLabel>
          <Select
            labelId="product-type-select-label"
            id="product-type-select"
            value={filters.productType || 'All'}
            label="Product Type"
            notched
            displayEmpty
            onChange={(event) => {
              // When product type changes, reset the edition filter
              onUpdateFilters({ 
                productType: event.target.value as 'Pulse' | 'Pulse X' | 'All',
                edition: '' // Reset edition when product type changes
              });
            }}
          >
            <MenuItem value="All">All Products</MenuItem>
            {productTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth size="small" sx={selectSx}>
          <InputLabel id="edition-select-label" shrink>Edition</InputLabel>
          <Select
            labelId="edition-select-label"
            id="edition-select"
            value={filters.edition || ''}
            label="Edition"
            notched
            displayEmpty
            renderValue={(value) => value || "All Editions"}
            onChange={(event) => onUpdateFilters({ edition: event.target.value })}
          >
            <MenuItem value="">All Editions</MenuItem>
            {editions.map((edition) => (
              <MenuItem key={edition} value={edition}>
                {edition}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth size="small" sx={selectSx}>
          <InputLabel id="flavors-select-label" shrink>Flavors</InputLabel>
          <Select
            labelId="flavors-select-label"
            id="flavors-select"
            multiple
            value={filters.categories || []}
            onChange={handleCategoryChange}
            displayEmpty
            input={<OutlinedInput label="Flavors" notched />}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return "All Flavors";
              }
              return selected.join(', ');
            }}
          >
            {categories.map((category) => (
              <MenuItem key={category} value={category}>
                <Checkbox checked={(filters.categories || []).indexOf(category) > -1} />
                <ListItemText primary={category} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth size="small" sx={selectSx}>
          <InputLabel id="intensity-select-label" shrink>Intensity</InputLabel>
          <Select
            labelId="intensity-select-label"
            id="intensity-select"
            value={filters.intensity || ''}
            label="Intensity"
            notched
            displayEmpty
            renderValue={(value) => value || "Any Intensity"}
            onChange={handleIntensityChange}
          >
            <MenuItem value="">Any Intensity</MenuItem>
            <MenuItem value="mild">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography>Mild</Typography>
                <Stack direction="row" sx={{ color: 'orange' }}>
                  <LocalFireDepartmentIcon fontSize="small" />
                </Stack>
              </Stack>
            </MenuItem>
            <MenuItem value="medium">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography>Medium</Typography>
                <Stack direction="row" sx={{ color: 'orange' }}>
                  <LocalFireDepartmentIcon fontSize="small" />
                  <LocalFireDepartmentIcon fontSize="small" />
                  <LocalFireDepartmentIcon fontSize="small" />
                </Stack>
              </Stack>
            </MenuItem>
            <MenuItem value="strong">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography>Strong</Typography>
                <Stack direction="row" sx={{ color: 'orange' }}>
                  <LocalFireDepartmentIcon fontSize="small" />
                  <LocalFireDepartmentIcon fontSize="small" />
                  <LocalFireDepartmentIcon fontSize="small" />
                  <LocalFireDepartmentIcon fontSize="small" />
                  <LocalFireDepartmentIcon fontSize="small" />
                </Stack>
              </Stack>
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
      
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={filters.hasCooling || false}
              onChange={handleCoolingChange}
              name="cooling"
              color="primary"
            />
          }
          label="Cooling Effect"
        />
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={filters.isNewArrival || false}
              onChange={handleNewArrivalsChange}
              name="newArrivals"
              color="secondary"
            />
          }
          label="New Arrivals Only"
        />
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth size="small" sx={selectSx}>
          <InputLabel id="rating-select-label" shrink>Minimum Rating</InputLabel>
          <Select
            labelId="rating-select-label"
            id="rating-select"
            value={filters.minRating?.toString() || ''}
            label="Minimum Rating"
            notched
            displayEmpty
            renderValue={(value) => value || "Any Rating"}
            onChange={(event) => {
              const value = event.target.value;
              onUpdateFilters({ 
                minRating: value ? parseFloat(value) : undefined 
              });
            }}
          >
            <MenuItem value="">Any Rating</MenuItem>
            <MenuItem value="4.5">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography>4.5+</Typography>
                <Rating value={4.5} readOnly precision={0.5} size="small" />
              </Stack>
            </MenuItem>
            <MenuItem value="4">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography>4.0+</Typography>
                <Rating value={4} readOnly precision={0.5} size="small" />
              </Stack>
            </MenuItem>
            <MenuItem value="3.5">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography>3.5+</Typography>
                <Rating value={3.5} readOnly precision={0.5} size="small" />
              </Stack>
            </MenuItem>
            <MenuItem value="3">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography>3.0+</Typography>
                <Rating value={3} readOnly precision={0.5} size="small" />
              </Stack>
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Paper>
  );
};

export default FilterPanel;
